import { Inject, Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { Course } from "../_models/Course";
import { catchError } from "rxjs/operators";
import { BaseService } from "./base-service";
import {
  AddLearnerCourseBatch,
  LearnerCourse,
  LearnerCoursesList,
  UpdateLearnerCourseDisabledDto,
  UpdateLearnerCourseDto,
} from "../_models/LearnerCourse";
import { APP_CONFIG } from "../lib/angular/injection-tokens";
import { IAppConfig } from "../lib/config/app-config";
import { MessageResultDto } from "../_models/MessageResultDto";
import { AppConstants } from "../constants/constants";

@Injectable({
  providedIn: "root",
})
export class LearnerCourseService extends BaseService {
  private baseUrl: string = this.config.protectedResourceEndpoint;
  private static readonly path: string = "LearnerCourse/";
  private static readonly UPDATEDISABLELEARNERCOURSE_URI: string =
    "updatedisablelearnercourse/";
  private static readonly GETLEARNERCOURSESLIST_URI: string =
    "getlearnercourseslist/";
  private static readonly GETBYLEARNERANDCOURSEID_URI: string =
    "getbylearnerandcourseid/";
  private static readonly ASSIGN_COURSE_MULT_LEARNERS: string =
    "assigncoursetomultiplelearners";
  private static readonly LEARNER_SCREENER_RESULTS: string =
    "learner/screener-results";
  constructor(
    http: HttpClient,
    @Inject(APP_CONFIG) private config: IAppConfig
  ) {
    super(http);
  }

  public addLearnerCourse$(learnerCourse: LearnerCourse) {
    return this.http.post(
      this.baseUrl + LearnerCourseService.path,
      learnerCourse
    );
  }

  public getLearnerCoursesByLanguage$(langCode: string): Observable<Course[]> {
    let params = new HttpParams().set("langId", langCode);

    return this.http
      .get<Course[]>(this.baseUrl + LearnerCourseService.path + "learner/", {
        params,
      })
      .pipe(catchError(this.handleError));
  }

  public getLearnerCoursesByLanguageAlt$(
    langCode: string
  ): Observable<LearnerCourse[]> {
    let params = new HttpParams().set("langId", langCode);

    return this.http
      .get<LearnerCourse[]>(
        this.baseUrl + LearnerCourseService.path + "learnercourses/",
        { params }
      )
      .pipe(catchError(this.handleError));
  }

  public updateLearnerCourseToggle$(
    updateDisable: UpdateLearnerCourseDisabledDto
  ): Observable<MessageResultDto> {
    return this.http.put<MessageResultDto>(
      this.baseUrl +
        LearnerCourseService.path +
        LearnerCourseService.UPDATEDISABLELEARNERCOURSE_URI,
      updateDisable
    );
  }

  public getLearnerCoursesList$(
    learnerId: number,
    langCode: string
  ): Observable<LearnerCoursesList[]> {
    let params = new HttpParams().set("langId", langCode);
    params = params.set("learnerId", String(learnerId));

    return this.http
      .get<LearnerCoursesList[]>(
        this.baseUrl +
          LearnerCourseService.path +
          LearnerCourseService.GETLEARNERCOURSESLIST_URI,
        {
          params,
        }
      )
      .pipe(catchError(this.handleError));
  }

  public getByLearnerAndCourseId$(
    learnerId: number,
    courseId: number
  ): Observable<LearnerCourse> {
    return this.http.get<LearnerCourse>(
      this.baseUrl +
        LearnerCourseService.path +
        LearnerCourseService.GETBYLEARNERANDCOURSEID_URI +
        learnerId +
        AppConstants.SLASH +
        courseId
    );
  }

  public updateLearnerCourse$(
    update: UpdateLearnerCourseDto
  ): Observable<UpdateLearnerCourseDto> {
    return this.http.put<UpdateLearnerCourseDto>(
      `${this.baseUrl}${LearnerCourseService.path}${update.id}`,
      update
    );
  }

  public addCourseToMultipleLearners$(
    list: AddLearnerCourseBatch[]
  ): Observable<boolean> {
    return this.http.post<boolean>(
      this.baseUrl +
        LearnerCourseService.path +
        LearnerCourseService.ASSIGN_COURSE_MULT_LEARNERS,
      list
    );
  }

  public getLearnerCourseScreenerResult$(langId: string): Observable<Course[]> {
    const params = new HttpParams().set(AppConstants.LANG_ID, langId);
    return this.http.get<Course[]>(`${this.baseUrl}${LearnerCourseService.path}${LearnerCourseService.LEARNER_SCREENER_RESULTS}`, {params: params});
  }
}
